"use client"

import { updateLineItem } from "@lib/data/cart"
import { changeSizeAction } from "@modules/cart/actions"
import { convertToLocale } from "@lib/util/money"
import { getProductUrl } from "@lib/util/product"
import { HttpTypes } from "@medusajs/types"
import SizeChangePopup from "@modules/cart/components/size-change-popup"
import LineItemCustomizationEditor from "@modules/cart/components/line-item-customization-editor"
import { getSizeFromVariant } from "@modules/cart/utils"
import ErrorMessage from "@modules/checkout/components/error-message"
import DeleteButton from "@modules/common/components/delete-button"
import LineItemPrice from "@modules/common/components/line-item-price"
import LineItemUnitPrice from "@modules/common/components/line-item-unit-price"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Spinner from "@modules/common/icons/spinner"
import Thumbnail from "@modules/products/components/thumbnail"
import useToggleState from "@lib/hooks/use-toggle-state"
import { Minus, Plus } from "lucide-react"
import { useState } from "react"

type ItemProps = {
  item: HttpTypes.StoreCartLineItem
  type?: "full" | "preview"
  currencyCode: string
  countryCode?: string
  /** Variant IDs already present in the cart (for the size popup) */
  cartVariantIds?: string[]
}

const Item = ({ item, type = "full", currencyCode, countryCode = "gb", cartVariantIds = [] }: ItemProps) => {
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editingCustomization, setEditingCustomization] = useState(false)
  const { state: sizePopupOpen, open: openSizePopup, close: closeSizePopup } = useToggleState()

  const changeQuantity = async (quantity: number) => {
    setError(null)
    setUpdating(true)

    const result = await updateLineItem({
      lineId: item.id,
      quantity,
    })

    if (!result.success) {
      setError(result.error || "Failed to update quantity")
    }

    setUpdating(false)
  }

  const changeSize = async (newVariantId: string) => {
    if (newVariantId === item.variant_id) return
    setError(null)
    setUpdating(true)

    const result = await changeSizeAction({
      lineId: item.id,
      originalVariantId: item.variant_id!,
      newVariantId,
      quantity: item.quantity,
      metadata: item.metadata ?? null,
    })

    if (!result.success) {
      setError(result.error || "Failed to change size")
    }

    setUpdating(false)
  }

  const maxQuantity = 10
  const currentSize = item.variant ? getSizeFromVariant(item.variant) : item.variant_title
  const productId = item.product_id

  if (type === "preview") {
    return (
      <tr className="w-full border-b border-gray-200" data-testid="product-row">
        <td className="!pl-0 p-4 w-24">
          <LocalizedClientLink
            href={getProductUrl(item.product_handle)}
            className="flex w-16"
          >
            <Thumbnail
              thumbnail={item.thumbnail}
              images={item.variant?.product?.images}
              size="square"
            />
          </LocalizedClientLink>
        </td>
        <td className="text-left p-4">
          <span className="text-sm font-medium text-gray-900" data-testid="product-title">
            {item.product_title}
          </span>
          <span className="block text-sm text-gray-500">
            Variant: {item.variant_title}
          </span>
        </td>
        <td className="!pr-0 p-4">
          <span className="flex flex-col items-end h-full justify-center">
            <span className="flex gap-x-1">
              <span className="text-gray-400">{item.quantity}x </span>
              <LineItemUnitPrice item={item} style="tight" currencyCode={currencyCode} />
            </span>
            <LineItemPrice item={item} style="tight" currencyCode={currencyCode} />
          </span>
        </td>
      </tr>
    )
  }

  // Price calculations
  const unitOriginal = (item.original_total ?? 0) / item.quantity
  const unitCurrent = (item.total ?? 0) / item.quantity
  const hasDiscount = unitCurrent < unitOriginal

  return (
    <div className="py-4 md:py-8" data-testid="product-row">
      <div className="flex gap-3 md:gap-6 md:items-center">
        {/* Product Image */}
        <LocalizedClientLink
          href={getProductUrl(item.product_handle)}
          className="relative w-[72px] h-[72px] md:w-[100px] md:h-[100px] flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden"
        >
          <Thumbnail
            thumbnail={item.thumbnail}
            images={item.variant?.product?.images}
            size="square"
          />
        </LocalizedClientLink>

        {/* Content Block */}
        <div className="flex-1 min-w-0">
          {/* Row 1: Name + Mobile Price */}
          <div className="flex items-start justify-between gap-2">
            <div>
              <LocalizedClientLink
                href={getProductUrl(item.product_handle)}
                className="text-sm md:text-base font-bold text-gray-900 line-clamp-2 hover:text-brand-primary transition-colors"
                data-testid="product-title"
              >
                {item.product_title}
              </LocalizedClientLink>
            </div>

            {/* Mobile price */}
            <div className="flex-shrink-0 text-right md:hidden">
              {hasDiscount && (
                <span className="block text-xs line-through text-rose-400">
                  {convertToLocale({ amount: unitOriginal, currency_code: currencyCode })}
                </span>
              )}
              <span className="text-sm font-bold text-gray-900">
                {convertToLocale({ amount: unitCurrent, currency_code: currencyCode })}
              </span>
            </div>
          </div>

          {/* Edit customisation button — expands inline, no page navigation */}
          <button
            type="button"
            onClick={() => setEditingCustomization((prev) => !prev)}
            className="mt-2 inline-block px-3 md:px-4 py-1 text-[10px] md:text-xs font-semibold text-white bg-brand-primary rounded-full hover:bg-brand-navy transition-colors"
          >
            Edit customisation
          </button>

          {editingCustomization && (
            <LineItemCustomizationEditor
              item={item}
              currencyCode={currencyCode}
              onClose={() => setEditingCustomization(false)}
            />
          )}

          {/* Mobile: Size + Quantity + Remove row */}
          <div className="flex items-center gap-3 mt-2.5 md:hidden flex-wrap">
            {/* Size label + Change button */}
            {currentSize && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">
                  Size: <span className="font-medium text-gray-700">{currentSize}</span>
                </span>
                {productId && (
                  <button
                    type="button"
                    onClick={openSizePopup}
                    disabled={updating}
                    className="text-[10px] font-semibold text-brand-primary hover:text-brand-primary-dark underline disabled:opacity-40"
                  >
                    Change
                  </button>
                )}
              </div>
            )}

            {/* Quantity +/- */}
            <div className="flex items-center border border-gray-300 rounded">
              <button
                className="w-7 h-7 flex items-center justify-center text-gray-600 disabled:opacity-40"
                onClick={() => changeQuantity(item.quantity - 1)}
                disabled={item.quantity <= 1 || updating}
                aria-label="Decrease quantity"
              >
                <Minus className="w-3 h-3" />
              </button>
              <span className="w-7 h-7 flex items-center justify-center text-xs border-x border-gray-300">
                {item.quantity}
              </span>
              <button
                className="w-7 h-7 flex items-center justify-center text-gray-600 disabled:opacity-40"
                onClick={() => changeQuantity(item.quantity + 1)}
                disabled={item.quantity >= maxQuantity || updating}
                aria-label="Increase quantity"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>

            {updating && <Spinner />}
            <DeleteButton id={item.id}>Remove</DeleteButton>
          </div>
        </div>

        {/* Desktop: Size + Change button */}
        <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
          <span className="text-sm text-gray-700">Size:</span>
          <span className="text-sm font-medium text-gray-900">{currentSize}</span>
          {productId && (
            <button
              type="button"
              onClick={openSizePopup}
              disabled={updating}
              className="text-xs font-semibold text-brand-primary hover:text-brand-primary-dark underline disabled:opacity-40"
            >
              Change
            </button>
          )}
        </div>

        {/* Desktop: Quantity +/- */}
        <div className="hidden md:flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center border border-gray-300 rounded">
            <button
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={() => changeQuantity(item.quantity - 1)}
              disabled={item.quantity <= 1 || updating}
              aria-label="Decrease quantity"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span className="w-8 h-8 flex items-center justify-center text-sm border-x border-gray-300">
              {item.quantity}
            </span>
            <button
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={() => changeQuantity(item.quantity + 1)}
              disabled={item.quantity >= maxQuantity || updating}
              aria-label="Increase quantity"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          {updating && <Spinner />}
        </div>

        {/* Desktop: Price */}
        <div className="hidden md:block flex-shrink-0 text-right">
          {hasDiscount && (
            <span className="block text-sm line-through text-rose-400">
              {convertToLocale({ amount: unitOriginal, currency_code: currencyCode })}
            </span>
          )}
          <span className="text-lg font-bold text-gray-900">
            {convertToLocale({ amount: unitCurrent, currency_code: currencyCode })}
          </span>
        </div>

        {/* Desktop: Remove Link */}
        <div className="hidden md:block flex-shrink-0">
          <DeleteButton id={item.id}>Remove</DeleteButton>
        </div>
      </div>

      <ErrorMessage error={error} data-testid="product-error-message" />

      {productId && item.variant_id && (
        <SizeChangePopup
          isOpen={sizePopupOpen}
          close={closeSizePopup}
          productId={productId}
          productName={item.product_title || ""}
          currentVariantId={item.variant_id}
          cartVariantIds={cartVariantIds}
          countryCode={countryCode}
          onSelectSize={changeSize}
        />
      )}
    </div>
  )
}

export default Item
